import React, { useContext, useEffect, useRef, useState } from "react";
import {
  BarChart3,
  BookOpen,
  LifeBuoy,
  LogOut,
  Monitor,
  Moon,
  Palette,
  SettingsIcon,
  Sun,
  Wrench,
} from "lucide-react";
import { useTheme } from "next-themes";
import posthog from "posthog-js";

import { AuthContext } from "../AuthProvider";
import { githubLogin } from "@/lib/github-login";
import { NotificationFloatingButton } from "../NotificationFloatingButton";
import { StatusWidgetMorph, RailStatusHoverCard } from "./StatusIndicator";
import { BSErrorBoundary } from "../ui/bs-error-boundary";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuControlOption,
  DropdownMenuControlRow,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/blacksmith-ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@repo/blacksmith-ui/tooltip";
import { Button } from "@repo/blacksmith-ui/button";
import { RoutePrefetchLink } from "@/components/ui/route-prefetch-link";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { useRoutePrefetchIntent } from "@/hooks/useRoutePrefetchIntent";
import { useIsHydrated } from "@/components/codesmith/useIsHydrated";
import { cn } from "@repo/blacksmith-ui/cn";
import { RAIL_REDESIGN_WIDTH, RAIL_WIDTH } from "./railConstants";

export type SidebarActiveSection =
  | "home"
  | "actions"
  | "codesmith"
  | "proof"
  | "sandboxes"
  | "usage"
  | "admin";

export const RAIL_ROW = cn(
  "rail-row flex h-8 w-full items-center overflow-hidden rounded-md cursor-pointer transition-none",
  "type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950",
  "[&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900",
  "dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]",
);

export const RAIL_ROW_ACTIVE =
  "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";

export const RAIL_REDESIGN_CLASS =
  "[&_.rail-row]:rounded-[4px] [&_.rail-row-icon]:w-full [&_.rail-row-icon>svg]:size-4";

export function RailRowContents({
  icon,
  label,
  badge,
  expanded = true,
  hideLabelWhenCollapsed = true,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
  expanded?: boolean;
  // The rail's collapse is an animated width wipe, so its labels and badges
  // must stay in the DOM and let the shrinking `overflow-hidden` row clip
  // them. Only the icon-collapsible sidebar (an instant, non-animated snap)
  // wants them removed outright.
  hideLabelWhenCollapsed?: boolean;
}) {
  const collapsedAndRemoved = hideLabelWhenCollapsed && !expanded;
  return (
    <>
      <span className="rail-row-icon flex size-8 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span
        className={cn(
          "whitespace-nowrap text-left",
          collapsedAndRemoved && "hidden",
          !badge && "pr-2",
        )}
      >
        {label}
      </span>
      {badge && !collapsedAndRemoved ? (
        <span className="ml-auto mr-2 shrink-0">{badge}</span>
      ) : null}
    </>
  );
}

const THEME_OPTIONS = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
] as const;

export function AppearanceThemeToggle() {
  const { theme } = useTheme();
  const setTheme = useSetPersistedTheme();
  const mounted = useIsHydrated();

  const activeTheme = mounted ? (theme ?? "system") : "system";

  return (
    <DropdownMenuRadioGroup
      value={activeTheme}
      onValueChange={setTheme}
      className="-mr-1 flex items-center gap-0.5"
    >
      {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
        <DropdownMenuControlOption
          key={value}
          value={value}
          aria-label={label}
          textValue={label}
        >
          <Icon />
        </DropdownMenuControlOption>
      ))}
    </DropdownMenuRadioGroup>
  );
}

// A right-anchored label tooltip for a collapsed rail/sidebar row. Used both by
// the icon-collapsible sidebar and by the slim app-switcher rail (which no
// longer hover-expands to reveal labels, so each row surfaces its label here).
export function CollapsedIconTooltip({
  label,
  enabled,
  shortcut,
  children,
}: {
  label: string;
  enabled: boolean;
  shortcut?: string;
  children: React.ReactElement;
}) {
  if (!enabled) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {shortcut ? (
        <TooltipContent side="right" className="flex items-center gap-2">
          <span className="leading-none">{label}</span>
          <ShortcutKeys keys={[shortcut]} />
        </TooltipContent>
      ) : (
        <TooltipContent side="right">{label}</TooltipContent>
      )}
    </Tooltip>
  );
}

function useSidebarLogout() {
  const { logout } = useContext(AuthContext);

  return () => {
    posthog?.reset();
    logout();
  };
}

export function SidebarAdminRow({
  expanded,
  active,
  onClick,
  hideLabelWhenCollapsed = true,
  divider = true,
}: {
  expanded: boolean;
  active: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  hideLabelWhenCollapsed?: boolean;
  divider?: boolean;
}) {
  const { user } = useContext(AuthContext);
  if (!user?.is_superuser) return null;

  return (
    <>
      {divider && (
        <div className="my-2">
          <hr className="border-[#0c0c0d14] dark:border-white/12" />
        </div>
      )}
      <CollapsedIconTooltip
        label="Admin"
        enabled={hideLabelWhenCollapsed && !expanded}
      >
        <RoutePrefetchLink
          href="/admin"
          onClick={onClick}
          aria-label="Admin"
          aria-current={active ? "page" : undefined}
          className={cn(
            RAIL_ROW,
            active && RAIL_ROW_ACTIVE,
            // Keyed off data-collapsible, not data-state: the app-switcher
            // rail is also a `group` with data-state="collapsed", and these
            // width locks would snap its label away instead of letting the
            // hover wipe clip it. Only the shadcn icon sidebar sets
            // data-collapsible="icon".
            "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:justify-center",
          )}
        >
          <RailRowContents
            icon={<Wrench className="size-[18px]" />}
            label="Admin"
            expanded={expanded}
            hideLabelWhenCollapsed={hideLabelWhenCollapsed}
          />
        </RoutePrefetchLink>
      </CollapsedIconTooltip>
    </>
  );
}

export function SidebarGlobalFooter({
  expanded,
  currentOrg,
  settingsHref,
  activeSection,
  onSectionClick,
  className,
  hideLabelWhenCollapsed = true,
  statusAsHoverCard = false,
  redesign = false,
  leading,
}: {
  expanded: boolean;
  currentOrg?: string | null;
  settingsHref: string;
  activeSection?: SidebarActiveSection | null;
  onSectionClick?: (
    section: Extract<SidebarActiveSection, "usage">,
  ) => (event: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
  hideLabelWhenCollapsed?: boolean;
  /** Render the vCPU/status widget as an interactive hover overlay that morphs
   * only that portion open, instead of morphing inline with the container.
   * Used by the slim app-switcher rail that no longer expands as a whole. */
  statusAsHoverCard?: boolean;
  redesign?: boolean;
  leading?: React.ReactNode;
}) {
  const { user } = useContext(AuthContext);
  const login = githubLogin(user);
  const handleLogout = useSidebarLogout();

  // Usage is the one destination whose route chunk (four Recharts surfaces,
  // billing tables, Luxon) nothing else on the dashboard loads, so an
  // unprefetched visit pays for it at click time (p50 0.26 s against ~20 ms
  // elsewhere). The full viewport prefetch pulls it down while idle.
  const usageHref = currentOrg ? `/${currentOrg}/usage` : "/usage";
  const supportHref = currentOrg ? `/${currentOrg}/support` : "/support";

  // The settings menu opens on hover. Closing is deferred a beat so the
  // pointer can cross the sideOffset gap between the trigger and the menu
  // without collapsing it. Keyboard toggling still works through the regular
  // Radix trigger; a click on the gear is a no-op, and the menu's Settings
  // item is the way to the settings page. Touch has no hover, and a tap fires
  // pointerleave as the finger lifts, so touch skips the hover handlers and
  // falls through to Radix's tap-to-toggle.
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsTriggerRef = useRef<HTMLButtonElement>(null);
  const settingsPrefetchIntent = useRoutePrefetchIntent(
    currentOrg ? settingsHref : null,
  );
  const settingsMenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const cancelSettingsMenuClose = () => {
    if (settingsMenuCloseTimer.current) {
      clearTimeout(settingsMenuCloseTimer.current);
      settingsMenuCloseTimer.current = null;
    }
  };
  const openSettingsMenu = (event: React.PointerEvent) => {
    if (event.pointerType === "touch") return;
    cancelSettingsMenuClose();
    setSettingsMenuOpen(true);
  };
  const scheduleSettingsMenuClose = (event: React.PointerEvent) => {
    if (event.pointerType === "touch") return;
    cancelSettingsMenuClose();
    settingsMenuCloseTimer.current = setTimeout(
      () => setSettingsMenuOpen(false),
      120,
    );
  };
  useEffect(() => cancelSettingsMenuClose, []);

  const notifications = (
    <NotificationFloatingButton
      variant="sidebar"
      label="Notifications"
      expanded={expanded}
      hideLabelWhenCollapsed={hideLabelWhenCollapsed}
    />
  );

  const status = (
    <BSErrorBoundary title="Status Indicator" fallback={null}>
      <div className={redesign ? undefined : "pt-1"}>
        {statusAsHoverCard ? (
          <RailStatusHoverCard
            railWidth={redesign ? RAIL_REDESIGN_WIDTH : RAIL_WIDTH}
          />
        ) : (
          <StatusWidgetMorph expanded={expanded} />
        )}
      </div>
    </BSErrorBoundary>
  );

  const settingsMenu = (
    <DropdownMenu
      modal={false}
      open={settingsMenuOpen}
      onOpenChange={setSettingsMenuOpen}
    >
      <DropdownMenuTrigger asChild>
        <button
          ref={settingsTriggerRef}
          type="button"
          aria-label="Settings menu"
          className={RAIL_ROW}
          onPointerEnter={openSettingsMenu}
          onPointerLeave={scheduleSettingsMenuClose}
          // Hovering the gear opens a menu whose first item is Settings, so
          // warm that route now.
          onMouseEnter={settingsPrefetchIntent.onMouseEnter}
          onFocus={settingsPrefetchIntent.onFocus}
          // The menu opens on hover, so by the time the gear is clicked it is
          // already open, and clicking it must not toggle it shut. Radix
          // toggles on the trigger's own pointer-down; suppressing the default
          // here skips that toggle (Radix composes after this handler and
          // bails on a prevented default). The non-modal dismiss layer is the
          // other close path and is handled on the content below. Matches
          // Radix's left-button, no-ctrl condition so right-click, keyboard and
          // touch toggling are untouched.
          onPointerDown={(event) => {
            if (
              settingsMenuOpen &&
              event.pointerType !== "touch" &&
              event.button === 0 &&
              !event.ctrlKey
            ) {
              event.preventDefault();
            }
          }}
        >
          <RailRowContents
            icon={<SettingsIcon className="size-[18px]" />}
            label="Settings"
            expanded={expanded}
            hideLabelWhenCollapsed={hideLabelWhenCollapsed}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={8}
        // Shifted up one row so the second item (Contact Support) centers on
        // the 32px trigger: border (1px) + padding (4px) + one 28px row,
        // less the 2px that centers a 28px row on it.
        alignOffset={-31}
        className="min-w-[216px] max-w-[320px]"
        onPointerEnter={openSettingsMenu}
        onPointerLeave={scheduleSettingsMenuClose}
        // Closing a hover-opened menu must not refocus the trigger, or the
        // gear keeps a keyboard focus ring after a mouse-only interaction.
        onCloseAutoFocus={(event) => event.preventDefault()}
        // In a non-modal menu, a pointer-down on the trigger also registers
        // as an outside interaction and dismisses the menu. Clicking the gear
        // that opened it should keep it open, so ignore dismissals whose
        // pointer-down originated on the trigger; clicks elsewhere still
        // close the menu as usual.
        onPointerDownOutside={(event) => {
          const target = event.target;
          if (
            target instanceof Node &&
            settingsTriggerRef.current?.contains(target)
          ) {
            event.preventDefault();
          }
        }}
      >
        {/* Navigation items are real anchors (asChild) so modified clicks
            (cmd/ctrl, middle-click) open in a new tab natively. */}
        <DropdownMenuItem asChild icon={<SettingsIcon />}>
          <RoutePrefetchLink
            href={settingsHref}
            prefetchOnIntent={!!currentOrg}
          >
            Settings
          </RoutePrefetchLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild icon={<LifeBuoy />}>
          <RoutePrefetchLink href={supportHref} prefetchOnIntent={!!currentOrg}>
            Contact Support
          </RoutePrefetchLink>
        </DropdownMenuItem>
        <DropdownMenuItem asChild icon={<BookOpen />}>
          <a
            href="https://docs.blacksmith.sh"
            target="_blank"
            rel="noopener noreferrer"
          >
            Documentation...
          </a>
        </DropdownMenuItem>
        <DropdownMenuControlRow icon={<Palette />} label="Appearance">
          <AppearanceThemeToggle />
        </DropdownMenuControlRow>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut />}
          trailing={
            login ? (
              <span className="block max-w-[200px] truncate" title={login}>
                {login}
              </span>
            ) : undefined
          }
          onClick={handleLogout}
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const usage = (
    <CollapsedIconTooltip
      label="Usage and Billing"
      enabled={hideLabelWhenCollapsed && !expanded}
    >
      <RoutePrefetchLink
        href={usageHref}
        prefetch={currentOrg ? true : null}
        prefetchOnIntent={!!currentOrg}
        onClick={onSectionClick?.("usage")}
        aria-label="Usage and Billing"
        aria-current={activeSection === "usage" ? "page" : undefined}
        className={cn(RAIL_ROW, activeSection === "usage" && RAIL_ROW_ACTIVE)}
      >
        <RailRowContents
          icon={<BarChart3 className="size-[18px]" />}
          label="Usage and Billing"
          expanded={expanded}
          hideLabelWhenCollapsed={hideLabelWhenCollapsed}
        />
      </RoutePrefetchLink>
    </CollapsedIconTooltip>
  );

  if (redesign) {
    return (
      <div className={cn("rail-rows flex flex-col", className)}>
        {leading}
        {usage}
        {notifications}
        {settingsMenu}
        {status}
      </div>
    );
  }

  return (
    <div className={cn("rail-rows flex flex-col gap-1", className)}>
      {usage}
      {settingsMenu}
      {notifications}
      {status}
    </div>
  );
}
